import { useCallback, useEffect, useState } from "react";
import * as services from "../services/employeeApi";
import { useDispatch, useSelector } from "react-redux";
import { setEmployeeWelfareList, setIsLoading } from "../slices/employeeSlice";
import { generateRandomString } from "utils/helper";
import { useParams } from "react-router-dom";
import { message, Modal } from "antd";
import { useTranslation } from "react-i18next";

function useEmployeeWelfare() {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const employeeWelfareList = useSelector((state) => state.employee.employeeWelfareList || []);
    const { currentCompany, currentEmployee } = useSelector((state) => state.company);
    const [pagination, setPagination] = useState({ page: 1, size: 10 });
    const [isVisible, setIsVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    const showModal = () => {
        setIsModalOpen(true);
    };

    const handleEdit = (record) => {
        setSelectedRecord(record);
        setIsModalOpen(true);
    };

    function toParams(params) {
        const { pagination = {} } = params;
        const modifiedParams = {
            ...params.pagination,
            page: pagination.page || 0,
            size: pagination.pageSize || 10,
        };
        return modifiedParams;
    }

    const getEmployeeWelfareList = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                const response = await services.getEmployeeWelfarePagination({
                    ...params,
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    employeeId: id
                });
                dispatch(setEmployeeWelfareList(response?.data?.page?.content || []));
                setPagination({
                    page: params.page + 1,
                    size: params.size,
                    total: response?.data?.page?.totalElements,
                });
            } catch (error) {
                alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [dispatch, currentCompany, currentEmployee, pagination]
    );

    const updateEmployeeWelfare = useCallback(
        async (values) => {
            try {
                dispatch(setIsLoading(true));
                await services.updateEmployeeWelfare(
                    {
                        // ...values,
                        requestId: generateRandomString(),
                        companyId: currentCompany,
                        employeeId: id,
                        stpWelfareId: selectedRecord?.id,
                        maxPerMonth: values?.maxPerMonth,
                        maxPerYear: values?.maxPerYear
                    }
                );
                setIsModalOpen(false);
                message.success(t("common.success"))
            } catch (error) {
                message.error(t("common.error"))
            } finally {
                dispatch(setIsLoading(false));
                getEmployeeWelfareList({ page: 0, size: 10 })
            }
        },
        [currentCompany, dispatch, id, selectedRecord?.id]
    );

    const onDelete = useCallback(
        async (record) => {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    employeeId: id,
                    stpWelfareId: record?.id,
                    statusFlag: "DELETE",
                };
                await services.deleteEmployeeWelfare(values);
                const modifiedParams = toParams({
                    page: pagination.page - 1,
                    size: pagination.size,
                });
                getEmployeeWelfareList(modifiedParams);
            } catch (error) {
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch, getEmployeeWelfareList]
    );

    const onStatus = useCallback(
        async (val, record) => {
            Modal.confirm({
                title: t("dialog.confirmation.header"),
                content: (i18n.language === "en"
                    ? <span>Are you sure confirm to change status ?</span>
                    : <span>คุณยืนยันการเปลี่ยนสถานะใช่หรือไม่ ?</span>
                ),
                okText: t("common.confirm"),
                cancelText: t("common.cancel"),
                onOk: async () => {
                    try {
                        dispatch(setIsLoading(true));
                        const values = {
                            requestId: generateRandomString(),
                            companyId: currentCompany,
                            employeeId: id,
                            stpWelfareId: record?.id,
                            statusFlag: val ? "Y" : "N",
                        };
                        await services.manageEmployeeWelfareStatus(values);
                        const modifiedParams = toParams({
                            page: pagination.page - 1,
                            size: pagination.size,
                        });
                        message.success(t("common.success"))
                        getEmployeeWelfareList(modifiedParams)
                    } catch (error) {
                        message.error(t("common.error"))
                    } finally {
                        dispatch(setIsLoading(false));
                    }
                }
            })
        },
        [t, i18n.language, dispatch, currentCompany, pagination.page, pagination.size, getEmployeeWelfareList]
    );

    const onChange = useCallback(
        (tablePagination, _) => {
            setPagination(tablePagination);

            const modifiedParams = toParams({
                pagination: {
                    page: tablePagination.current - 1,
                    pageSize: tablePagination.pageSize,
                },
            });
            getEmployeeWelfareList(modifiedParams);
        },
        [getEmployeeWelfareList]
    );

    useEffect(() => {
        getEmployeeWelfareList({ page: 0, size: 10 })
    }, [currentEmployee, currentCompany])

    return {
        isModalOpen,
        employeeWelfareList,
        pagination,
        isVisible,
        setIsVisible,
        setIsModalOpen,
        showModal,
        selectedRecord,
        setSelectedRecord,
        handleEdit,
        updateEmployeeWelfare,
        onDelete,
        onChange,
        onStatus
    }
}

export default useEmployeeWelfare;